'use client'

import { useState } from 'react'
import ReactMarkdown from 'react-markdown'
import Link from 'next/link'
import { Share } from 'lucide-react'
import type { InspirationPiece, InspirationBook } from '@/lib/inspiration'
import { CenteredDialog } from '@/components/CenteredDialog'

type InspirationContentProps = {
  piece: InspirationPiece
  book: InspirationBook
  showNavigation?: boolean
  totalPieces?: number
  allPieces?: InspirationPiece[]
  headingLevel?: 'h1' | 'h2'
}

export function InspirationContent({
  piece,
  book,
  showNavigation = false,
  totalPieces,
  allPieces,
  headingLevel = 'h2'
}: InspirationContentProps) {
  // Find prev/next piece slugs for navigation
  const currentIndex = allPieces?.findIndex(p => p.id === piece.id) ?? -1
  const prevPiece = currentIndex > 0 ? allPieces?.[currentIndex - 1] : null
  const nextPiece = currentIndex >= 0 && allPieces && currentIndex < allPieces.length - 1 ? allPieces[currentIndex + 1] : null
  const Heading = headingLevel
  const [showCopiedFeedback, setShowCopiedFeedback] = useState(false)
  const [showSignupOverlay, setShowSignupOverlay] = useState(false)

  const handleShare = async () => {
    const shareUrl = `https://homiehub.co/inspiration/${book.slug}/${piece.slug}`
    try {
      await navigator.clipboard.writeText(shareUrl)
      setShowCopiedFeedback(true)
      setTimeout(() => setShowCopiedFeedback(false), 2000)
    } catch (err) {
      const textArea = document.createElement('textarea')
      textArea.value = shareUrl
      document.body.appendChild(textArea)
      textArea.select()
      document.execCommand('copy')
      document.body.removeChild(textArea)
      setShowCopiedFeedback(true)
      setTimeout(() => setShowCopiedFeedback(false), 2000)
    }
  }

  return (
    <section className="app-form px-xxl py-xxl">
      {/* Separator before content */}
      {showNavigation && <hr className="border-t border-dashed border-black mb-l" />}

      {/* Navigation (for book reader) */}
      {showNavigation && totalPieces && (
        <div className="flex items-center justify-between mb-l">
          <span className="text-m text-text-inactive">
            {piece.piece_number} of {totalPieces}
          </span>
          <div className="flex items-center gap-m">
            {/* Heart Button - shows signup overlay */}
            <button
              onClick={() => setShowSignupOverlay(true)}
              className="w-11 h-11 flex items-center justify-center cursor-pointer"
              aria-label="Save"
            >
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M16.5 3C14.76 3 13.09 3.81 12 5.09C10.91 3.81 9.24 3 7.5 3C4.42 3 2 5.42 2 8.5C2 12.28 5.4 15.36 10.55 20.04L12 21.35L13.45 20.04C18.6 15.36 22 12.28 22 8.5C22 5.42 19.58 3 16.5 3ZM12.1 18.55L12 18.65L11.9 18.55C7.14 14.24 4 11.39 4 8.5C4 6.5 5.5 5 7.5 5C9.04 5 10.54 5.99 11.07 7.36H12.94C13.46 5.99 14.96 5 16.5 5C18.5 5 20 6.5 20 8.5C20 11.39 16.86 14.24 12.1 18.55Z" fill="currentColor"/>
              </svg>
            </button>
            {/* Share Button */}
            <button
              onClick={handleShare}
              className="w-11 h-11 flex items-center justify-center cursor-pointer"
              aria-label="Share"
            >
              <Share size={28} strokeWidth={2} />
            </button>
            {/* Previous Button */}
            {prevPiece ? (
              <Link
                href={`/inspiration/${book.slug}/${prevPiece.slug}`}
                scroll={false}
                className="w-11 h-11 flex items-center justify-center"
                aria-label="Previous"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.29 20L7.28999 12L15.29 4L16.71 5.42L10.13 12L16.71 18.58L15.29 20Z" fill="currentColor"/>
                </svg>
              </Link>
            ) : (
              <span className="w-11 h-11 flex items-center justify-center opacity-30">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M15.29 20L7.28999 12L15.29 4L16.71 5.42L10.13 12L16.71 18.58L15.29 20Z" fill="currentColor"/>
                </svg>
              </span>
            )}
            {/* Next Button */}
            {nextPiece ? (
              <Link
                href={`/inspiration/${book.slug}/${nextPiece.slug}`}
                scroll={false}
                className="w-11 h-11 flex items-center justify-center"
                aria-label="Next"
              >
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.70999 4L16.71 12L8.70999 20L7.28999 18.58L13.87 12L7.28999 5.42L8.70999 4Z" fill="currentColor"/>
                </svg>
              </Link>
            ) : (
              <span className="w-11 h-11 flex items-center justify-center opacity-30">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.70999 4L16.71 12L8.70999 20L7.28999 18.58L13.87 12L7.28999 5.42L8.70999 4Z" fill="currentColor"/>
                </svg>
              </span>
            )}
          </div>
        </div>
      )}

      {/* Link Copied Snackbar - matches app Snackbar component */}
      {showCopiedFeedback && (
        <div
          className="fixed left-0 right-0 z-50 bg-surface-primary border-t-m border-stroke-primary"
          style={{ bottom: 0 }}
        >
          <div className="flex items-center gap-[10px] px-xxl py-xxl">
            {/* Info Icon */}
            <div className="flex items-center shrink-0">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
                <path d="M12 8V8.01M12 12V16" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </div>

            {/* Message */}
            <p className="flex-1 text-m text-text-primary">
              Link copied!
            </p>

            {/* Close Button */}
            <button
              onClick={() => setShowCopiedFeedback(false)}
              className="flex items-center shrink-0 cursor-pointer"
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M6.4 19L5 17.6L10.6 12L5 6.4L6.4 5L12 10.6L17.6 5L19 6.4L13.4 12L19 17.6L17.6 19L12 13.4L6.4 19Z" fill="currentColor"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Content */}
      <div className="text-l leading-relaxed text-text-primary">
        {piece.quote ? (
          <>
            {/* H1: Use title if available, otherwise use "Quote by {author}" */}
            <Heading className="text-xl font-bold mb-m leading-tight">
              {piece.title || `Quote by ${piece.quote_by}`}
            </Heading>
            <blockquote className="border-l-[4px] border-stroke-light pl-[14px] md:pl-[28px] py-sm my-m">
              <p className="text-l leading-relaxed italic">
                {piece.quote}
              </p>
            </blockquote>
            <p className="text-m text-text-inactive leading-medium mt-m mb-xxl">
              — {piece.quote_by}
            </p>
            <ReactMarkdown
              components={{
                h2: ({children}) => <h2 className="text-h2 font-bold mt-xl mb-m leading-tight">{children}</h2>,
                h3: ({children}) => <h3 className="text-l font-bold mt-xxxl mb-m leading-tight">{children}</h3>,
                p: ({children}) => <p className="mb-m leading-relaxed">{children}</p>,
                blockquote: ({children}) => <blockquote className="border-l-[4px] border-stroke-light pl-[14px] md:pl-[28px] py-sm my-xl italic leading-relaxed">{children}</blockquote>,
                ul: ({children}) => <ul className="list-disc ml-[28px] mb-m leading-relaxed">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal ml-[28px] mb-m leading-relaxed">{children}</ol>,
                li: ({children}) => <li className="mb-s">{children}</li>
              }}
            >
              {piece.content}
            </ReactMarkdown>
          </>
        ) : (
          <>
            {piece.title && (
              <Heading className="text-xl font-bold mb-m leading-tight">
                {piece.title}
              </Heading>
            )}
            <ReactMarkdown
              components={{
                h2: ({children}) => <h2 className="text-h2 font-bold mt-xl mb-m leading-tight">{children}</h2>,
                h3: ({children}) => <h3 className="text-l font-bold mt-xxxl mb-m leading-tight">{children}</h3>,
                p: ({children}) => <p className="mb-m leading-relaxed">{children}</p>,
                blockquote: ({children}) => <blockquote className="border-l-[4px] border-stroke-light pl-[14px] md:pl-[28px] py-sm my-xl italic leading-relaxed">{children}</blockquote>,
                ul: ({children}) => <ul className="list-disc ml-[28px] mb-m leading-relaxed">{children}</ul>,
                ol: ({children}) => <ol className="list-decimal ml-[28px] mb-m leading-relaxed">{children}</ol>,
                li: ({children}) => <li className="mb-s">{children}</li>
              }}
            >
              {piece.content}
            </ReactMarkdown>
          </>
        )}
      </div>

      {/* Separator before CTA */}
      <hr className="border-t border-dashed border-black mt-[62px]" />

      {/* CTA for main app */}
      <div id="signup-cta" className="flex flex-col items-center gap-[16px] pt-[34px] pb-[48px]">
        {/* Heart icon */}
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
        </svg>

        {/* CTA Text */}
        <div className="text-center flex flex-col gap-[8px]">
          <h3 className="text-xl font-bold">Save your favorites</h3>
          <p className="text-l">Sign up for Homie to save favorites and get daily inspiration that is 100% algorithm-free.</p>
        </div>

        {/* Buttons */}
        <div className="flex gap-[12px]">
          {/* About - outline button */}
          <Link
            href="/"
            className="h-[60px] flex items-center justify-center px-[32px] py-[12px] bg-transparent border-2 border-black text-black text-[24px] font-bold rounded-[8px] cursor-pointer"
          >
            About
          </Link>

          {/* Sign Up - primary button */}
          <Link
            href="/signup"
            className="h-[60px] flex items-center justify-center px-[32px] py-[12px] bg-black text-[#F7F1E7] text-[24px] font-bold rounded-[8px] cursor-pointer"
          >
            Sign up
          </Link>
        </div>
      </div>

      {/* Signup Dialog */}
      <CenteredDialog isOpen={showSignupOverlay} onClose={() => setShowSignupOverlay(false)}>
        <div className="flex flex-col gap-xxxl">
          {/* Text Section */}
          <div className="flex flex-col gap-m items-center text-center">
            {/* Heart icon */}
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z"/>
            </svg>
            <h2 className="text-l font-bold text-text-primary">Save your favorites</h2>
            <p className="text-m text-text-primary">Sign up for Homie to save favorites and get daily inspiration that is 100% algorithm-free.</p>
          </div>

          {/* Buttons Section */}
          <div className="flex flex-col gap-xl">
            {/* Sign Up - primary button */}
            <Link
              href="/signup"
              className="w-full min-h-button-large bg-surface-invert rounded-m px-button-x py-m flex items-center justify-center cursor-pointer"
            >
              <span className="text-l font-bold text-text-invert">Sign up</span>
            </Link>

            {/* About - secondary button */}
            <Link
              href="/"
              className="w-full min-h-button-large bg-surface-primary border-m border-stroke-primary rounded-m px-button-x py-m flex items-center justify-center cursor-pointer"
            >
              <span className="text-l font-bold text-text-primary">About</span>
            </Link>
          </div>
        </div>
      </CenteredDialog>
    </section>
  )
}
